// Importar desde un PDF o fotos de la carta: el navegador prepara los archivos y el workflow
// "MiQRapp - Importar carta con IA" de n8n (n8n/miqrapp-importar-ia.js) los lee con IA.
// Lo que vuelve pasa por el mismo paso "Revisar" que un Excel: nada se guarda sin que el usuario lo vea.

export const AI_URL = "https://n8n.miqrapp.com/webhook/miqrapp/importar-ia";
export const AI_MAX_PHOTOS = 6;
export const AI_MAX_PDF_BYTES = 8 * 1024 * 1024;
const PHOTO_MAX_SIDE = 2000;
const PHOTO_QUALITY = 0.82;
// Las columnas que arma toTable, para saltear el paso "Columnas".
export const AI_HEADERS = ["Nombre", "Precio", "Categoría", "Descripción"];
export const AI_MAPPING = ["name", "price", "category", "description"];

export const isPdf = (file) => file.type === "application/pdf" || /\.pdf$/i.test(file.name);
export const isImage = (file) => file.type.startsWith("image/") || /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name);

// Revisa la selección: un PDF solo, o hasta AI_MAX_PHOTOS fotos. Devuelve un mensaje de error o "".
export const checkSelection = (files) => {
    if (!files.length) return "Elegí un PDF o fotos de tu carta.";
    const pdfs = files.filter(isPdf);
    if (files.some((f) => !isPdf(f) && !isImage(f))) return "Solo podemos leer PDF o fotos (JPG, PNG o WEBP).";
    if (pdfs.length > 1 || (pdfs.length && files.length > 1)) return "Subí un solo PDF, o fotos sueltas, pero no los dos juntos.";
    if (pdfs.length && pdfs[0].size > AI_MAX_PDF_BYTES) return "El PDF pesa más de 8 MB. Probá exportarlo más liviano o sacale fotos a la carta.";
    if (files.length > AI_MAX_PHOTOS) return `Podés subir hasta ${AI_MAX_PHOTOS} fotos por vez.`;
    return "";
}

const toBase64 = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
});

const loadImage = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("image")); };
    img.src = url;
});

// Achica la foto (las del celular pesan 3 a 8 MB) y la pasa a JPG.
const shrinkPhoto = async (file) => {
    let img;
    try {
        img = await loadImage(file);
    } catch (error) {
        throw new Error(`No pudimos abrir "${file.name}". Si es una foto de iPhone (HEIC), mandala como JPG o sacale una captura.`);
    }
    const scale = Math.min(1, PHOTO_MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", PHOTO_QUALITY));
    if (!blob) throw new Error(`No pudimos preparar "${file.name}".`);
    return { name: file.name, type: "image/jpeg", data: await toBase64(blob) };
}

export const prepareFiles = (files) => Promise.all(files.map(async (file) => isPdf(file)
    ? { name: file.name, type: "application/pdf", data: await toBase64(file) }
    : shrinkPhoto(file)));

// Manda los archivos a n8n y devuelve la lista de productos que leyó la IA.
export const readWithAI = async ({ idToken, files, signal }) => {
    let response;
    try {
        response = await fetch(AI_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ idToken, files }),
            signal
        });
    } catch (error) {
        if (error.name === "AbortError") throw error;
        throw new Error("No pudimos conectarnos. Revisá tu internet y probá de nuevo.");
    }
    let result = {};
    try {
        result = await response.json();
    } catch (error) {
        // Sin cuerpo: se usa el mensaje genérico de abajo.
    }
    if (!response.ok || !Array.isArray(result.productos)) {
        if (response.status === 413) throw new Error("Los archivos pesan demasiado. Probá con menos fotos o un PDF más liviano.");
        throw new Error(result.error || "La IA no pudo leer la carta. Probá de nuevo en unos minutos.");
    }
    return { productos: result.productos, incomplete: result.incompleta === true };
}

// Lo que devuelve la IA, como si fuera una planilla con la plantilla de MiQRapp.
export const toTable = (productos) => ({
    headers: AI_HEADERS,
    rows: productos.map((p) => [
        String(p.nombre ?? "").trim(),
        typeof p.precio === "number" && Number.isFinite(p.precio) ? String(p.precio) : "",
        String(p.categoria ?? "").trim(),
        String(p.descripcion ?? "").trim()
    ]).filter((r) => r[0])
});
